// What's on screen: the panel counts the trains in the map's view, not the
// whole network's, once part of the network is out of it. Zoomed into
// Belfast on the Ireland map, Dublin's 1,000 buses aren't "running" there.
//
// Counted when the camera stops and when trains arrive, never per frame:
// one pass over the trains, a box test each.

import type { LatLon, LineId, TrackedTrain, TrainId } from './types.ts';
import type { Network } from './gps-types.ts';

// [west, south, east, north], degrees. West may be under -180 or east over
// 180 on a map that wraps.
export type Box = readonly [west: number, south: number, east: number, north: number];

// How far past the view's edges a train still counts, as a fraction of the
// view's width and height: one just off the edge is still "here".
export const VIEW_MARGIN = 0.05;

// The view grown by margin (a fraction of its size) on every side.
export function viewBox(bounds: Box, margin = VIEW_MARGIN): Box {
  const [west, south, east, north] = bounds;
  const dx = (east - west) * margin;
  const dy = (north - south) * margin;
  return [west - dx, south - dy, east + dx, north + dy];
}

// Whether a point is in the box, the same place a world away included.
export function inBox(box: Box, p: LatLon): boolean {
  const [west, south, east, north] = box;
  if (p.lat < south || p.lat > north) return false;
  const span = east - west;
  return span >= 360 || (((p.lon - west) % 360) + 360) % 360 <= span;
}

// Trains per line, how many of them run by the timetable alone
// (TrainPrediction.scheduled) and how many are placed by their GPS
// (TrainPrediction.gps). The rest are estimated from predicted times.
export interface TrainCounts {
  readonly counts: Partial<Record<LineId, number>>;
  readonly scheduled: Partial<Record<LineId, number>>;
  readonly gps: Partial<Record<LineId, number>>;
}

// Where a train is: where it was last drawn, or before its first frame the
// station it's heading for.
function place(net: Network | null, t: TrackedTrain): LatLon | null {
  return t.pred.gpsPoint ?? t.motion.pos ?? net?.stations.get(t.pred.N) ?? null;
}

// Every live train by line, and those in the box (null: no view yet).
export function countTrains(
  trains: ReadonlyMap<TrainId, TrackedTrain>,
  net: Network | null,
  box: Box | null,
): { readonly all: TrainCounts; readonly inView: TrainCounts | null } {
  const counts: Partial<Record<LineId, number>> = {};
  const scheduled: Partial<Record<LineId, number>> = {};
  const inCounts: Partial<Record<LineId, number>> = {};
  const inScheduled: Partial<Record<LineId, number>> = {};
  const gps: Partial<Record<LineId, number>> = {};
  const inGps: Partial<Record<LineId, number>> = {};
  for (const t of trains.values()) {
    if (t.dead) continue;
    const id = t.pred.lineId;
    const timetabled = t.pred.scheduled === true;
    const fixed = t.pred.gps === true;
    counts[id] = (counts[id] ?? 0) + 1;
    if (timetabled) scheduled[id] = (scheduled[id] ?? 0) + 1;
    if (fixed) gps[id] = (gps[id] ?? 0) + 1;
    const p = box ? place(net, t) : null;
    if (!box || !p || !inBox(box, p)) continue;
    inCounts[id] = (inCounts[id] ?? 0) + 1;
    if (timetabled) inScheduled[id] = (inScheduled[id] ?? 0) + 1;
    if (fixed) inGps[id] = (inGps[id] ?? 0) + 1;
  }
  return {
    all: { counts, scheduled, gps },
    inView: box ? { counts: inCounts, scheduled: inScheduled, gps: inGps } : null,
  };
}
